import { useContext, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Skeleton, countryToISO, withStyles } from 'vopay';
import { BodyText, Button, Title } from 'UI';
import { SubAccountList } from 'shared/subAccountList';
import useAccountBearer from '@/components/Entities/hooks/useApi/useAccountBearer';
import AccountContext from '@/components/Entities/Accounts/AccountContext';
import PartnerAccountCreation from '@/components/Entities/Accounts/AccountCreation/PartnerAccountCreation/PartnerAccountCreation';
import { usePartnerData } from '@/providers/PartnerData.provider';
import AccountScopedApiProvider from './AccountScopedApiProvider';
import useAccountSubAccountsHostProps from './useAccountSubAccountsHostProps';
import styles from './SubAccountsTab.module.scss';

const cn = withStyles(styles);

/**
 * The account page's Sub-Accounts tab (spec 5.6.5, ID-13430): the shared Sub-Account List, scoped to
 * this account by its own bearer. The tab owns its chrome: the "Sub-Accounts" heading with the Add
 * Sub-Account button beside it, and no white container, since the tab card is the container. The cards
 * the design shows above the table are out of scope for this ticket. Add Sub-Account (ID-13797, spec 5.4)
 * opens the Add Account drawer already in sub-account mode under this account.
 */
export default function SubAccountsTab() {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.Account.Tabs' });
  const { t: tTab } = useTranslation('translations', { keyPrefix: 'Accounts.Account.SubAccountsTab' });
  const { accountId } = useParams<{ accountId: string }>();
  const { bearer, hasSettled } = useAccountBearer(accountId);
  const { balances } = useContext(AccountContext);
  const [isAddDrawerOpen, setIsAddDrawerOpen] = useState(false);

  const {
    response: { isRoleViewOnly, isRoleAdmin, isInsideSales },
  } = usePartnerData();

  // The list fetch already carries the status and the entitlement the button is gated on. The
  // sub-account limit is not checked here: the backend refuses a creation over it (spec 5.4). An
  // Inside Sales partner does not get it at all (product, 2026-10-09).
  const listedAccount = balances.find((item) => item.AccountID === accountId);

  const canAddSubAccount =
    isRoleAdmin &&
    !isInsideSales &&
    listedAccount?.AccountStatus === 'Active' &&
    Boolean(listedAccount.SubAccountsEnabled);

  // On a deep link the token is looked up after mount; the list fetches on mount, so it must not
  // render until the token is there to scope that fetch.
  if (!bearer && !hasSettled) return <Skeleton height={320} data-testid="subAccountsTabLoading" />;

  // The lookup has answered and there is no token to scope the list with — see TransactionsTab.
  if (!bearer) {
    return (
      <BodyText data-testid="subAccountsTabUnavailable">
        {t('unavailable', { defaultValue: "This account's data could not be loaded." })}
      </BodyText>
    );
  }

  return (
    <div className={cn('tab')}>
      <div className={cn('header')}>
        <Title level="3">{t('subaccounts', { defaultValue: 'Sub-Accounts' })}</Title>

        {canAddSubAccount && (
          <Button
            id="subAccounts-addSubAccount"
            colorScheme="primary"
            size="small"
            iconLeft="add"
            onClick={() => setIsAddDrawerOpen(true)}
          >
            {tTab('addSubAccountButton', { defaultValue: 'Add Sub-Account' })}
          </Button>
        )}
      </div>

      <AccountScopedApiProvider bearer={bearer} readOnly={isRoleViewOnly}>
        <ScopedSubAccountList accountId={accountId} />
      </AccountScopedApiProvider>

      {/* Outside the scoped provider: the drawer creates as the partner, through the partner api. On
          success it navigates to the new sub-account, which lands on the Details tab and unmounts this
          one, drawer included. */}
      {isAddDrawerOpen && listedAccount && (
        <PartnerAccountCreation
          parentAccount={{
            accountID: accountId,
            accountName: listedAccount.AccountName,
            // The listing names the country; the drawer keys the US phone requirement on alpha-2.
            country: countryToISO(listedAccount.Country),
            currency: listedAccount.Currency,
          }}
          onClose={() => setIsAddDrawerOpen(false)}
        />
      )}
    </div>
  );
}

type ScopedSubAccountListProps = {
  accountId: string;
};

// Under the scoped provider on purpose: the host props carry the row Permissions drawer's fetch and save,
// which read the api from context and so must resolve to the account-scoped one.
function ScopedSubAccountList({ accountId }: ScopedSubAccountListProps) {
  const hostProps = useAccountSubAccountsHostProps(accountId);

  // The tab's heading above and the card around it set the spacing; the filters need none of their own.
  return <SubAccountList {...hostProps}>{() => <SubAccountList.Table hasFiltersPadding={false} />}</SubAccountList>;
}
